/**
 * BulkActionBar Component
 * Toolbar for bulk file operations with gradient background
 */

import { forwardRef, useMemo } from 'react';
import { Group, Text, Button, ActionIcon } from '@/components/ui';
import { IconX, IconTrash, IconFolderSymlink, IconDownload } from '@tabler/icons-react';
import { getContrastingTextColor } from '@/utils/color';
import type { GradientVariant } from '@/components/ccms-components/types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface BulkActionBarProps {
  /** Number of selected files */
  selectedCount: number;
  /** Callback to delete selected files */
  onDelete?: () => void;
  /** Callback to move selected files */
  onMove?: () => void;
  /** Callback to download selected files */
  onDownload?: () => void;
  /** Callback to clear selection */
  onClear: () => void;
  /** Gradient variant for styling */
  gradientVariant?: GradientVariant;
  /** Additional className */
  className?: string;
}

/**
 * Bulk action toolbar component for selected files
 *
 * @example
 * ```tsx
 * <BulkActionBar
 *   selectedCount={5}
 *   onDelete={() => handleBulkDelete()}
 *   onMove={() => handleBulkMove()}
 *   onDownload={() => handleBulkDownload()}
 *   onClear={() => clearSelection()}
 * />
 * ```
 */
export const BulkActionBar = forwardRef<HTMLDivElement, BulkActionBarProps>(
  (props, ref) => {
    const {
      selectedCount,
      onDelete,
      onMove,
      onDownload,
      onClear,
      gradientVariant = 'primary',
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    if (selectedCount === 0) return null;

    const getGradientBackground = (): string => {
      switch (gradientVariant) {
        case 'primary':
          return 'linear-gradient(135deg, var(--mantine-color-blue-6), var(--mantine-color-blue-7))';
        case 'secondary':
          return 'linear-gradient(135deg, var(--mantine-color-violet-6), var(--mantine-color-violet-7))';
        default:
          return 'linear-gradient(135deg, var(--mantine-color-blue-6), var(--mantine-color-blue-7))';
      }
    };

    const gradientBg = getGradientBackground();
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    return (
      <div
        ref={ref}
        className={className}
        style={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100,
          background: getGradientBackground(),
          borderRadius: 'var(--mantine-radius-lg)',
          padding: '12px 20px',
          boxShadow: 'var(--mantine-shadow-xl)',
          minWidth: 400,
          animation: 'slideUp 0.3s ease-out',
        }}
      >
        <Group justify="space-between">
          {/* Selection Info */}
          <Group gap="sm">
            <Text size="sm" fw={600} c={autoTextColor} style={accessibilityStyles}>
              {selectedCount} {selectedCount === 1 ? 'file' : 'files'} selected
            </Text>
          </Group>

          {/* Actions */}
          <Group gap="xs">
            {onDownload && (
              <Button
                variant="default"
                size="sm"
                leftSection={<IconDownload size={14} />}
                onClick={onDownload}
              >
                Download
              </Button>
            )}

            {onMove && (
              <Button
                variant="light"
                size="sm"
                leftSection={<IconFolderSymlink size={14} />}
                onClick={onMove}
              >
                Move
              </Button>
            )}

            {onDelete && (
              <Button
                variant="light"
                size="sm"
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={onDelete}
              >
                Delete
              </Button>
            )}

            <ActionIcon
              variant="light"
              size="sm"
              onClick={onClear}
              aria-label="Clear selection"
            >
              <IconX size={16} />
            </ActionIcon>
          </Group>
        </Group>

        <style>
          {`
            @keyframes slideUp {
              from {
                transform: translate(-50%, 100%);
                opacity: 0;
              }
              to {
                transform: translate(-50%, 0);
                opacity: 1;
              }
            }
          `}
        </style>
      </div>
    );
  }
);

BulkActionBar.displayName = 'BulkActionBar';

export default BulkActionBar;
